'use client';

import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import {
	ChartLine,
	ChevronDown,
	ChevronUp,
	Home,
	LogOut,
	Mails,
	Menu,
	NotepadText,
	Package,
	Settings,
	Shapes,
	ShoppingBag,
	Ticket,
	Truck,
	User,
	UserCog,
	Users,
	Wallpaper,
	X,
} from 'lucide-react';
import { useMemo, useState, type ComponentType, type SVGProps } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CLIENT_ROUTES } from '../../../../shared/constants/routes.constants';
import { generatePath } from '../../../../shared/services/app.shared-service';
import {
	ACCESS_TOKEN_STORAGE_KEY,
	USER_STORAGE_KEY,
} from '../../constants/auth.constants';
import { useLocale } from '../../hooks/useLocale';
import { cn } from '../../lib/utils';
import { useLogout } from '../../services/auth.api';
import { useConfig } from '../../services/config.api';
import { useAuthStore } from '../../stores/useAuthStore';
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '../ui/navigation-menu';
import HeaderImageTitle from './HeaderImageTitle';

type AdminLink = {
	href: string;
	title: string;
	icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type AdminGroupedLinks = {
	title: string;
	links: AdminLink[];
};

export default function AdminHeader() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [menusOpen, setMenusOpen] = useState<Record<string, boolean>>({});
	const { data: config } = useConfig();
	const { mutateAsync: logout } = useLogout();

	const removeAuthenticated = useAuthStore(
		(state) => state.removeAuthenticated
	);
	const user = useAuthStore((state) => state.user);

	const { t } = useLocale('client.header');
	const navigate = useNavigate();

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const handleLogout = () => {
		localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
		localStorage.removeItem(USER_STORAGE_KEY);
		removeAuthenticated();
		navigate(generatePath({ route: [CLIENT_ROUTES.AUTH] }));
		logout();
	};

	const adminGroupedLinks: AdminGroupedLinks[] = useMemo(() => {
		const catalogGroup = {
			title: t('catalog'),
			links: [
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.PRODUCTS.BASE,
						],
					}),
					title: t('products'),
					icon: Package,
				},
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.CATEGORIES,
						],
					}),
					title: t('categories'),
					icon: Shapes,
				},
			],
		};

		if (config?.customPagesEnabled) {
			catalogGroup.links.push({
				href: generatePath({
					route: [
						CLIENT_ROUTES.ADMIN.BASE,
						CLIENT_ROUTES.ADMIN.ROUTES.CUSTOM_PAGES,
					],
				}),
				title: t('customPages'),
				icon: NotepadText,
			});
		}

		if (config?.postersEnabled) {
			catalogGroup.links.push({
				href: generatePath({
					route: [
						CLIENT_ROUTES.ADMIN.BASE,
						CLIENT_ROUTES.ADMIN.ROUTES.POSTERS,
					],
				}),
				title: t('posters'),
				icon: Wallpaper,
			});
		}

		const salesGroup = {
			title: t('salesManagement'),
			links: [
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.ORDERS,
						],
					}),
					title: t('orders'),
					icon: ShoppingBag,
				},
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.COUPONS,
						],
					}),
					title: t('coupons'),
					icon: Ticket,
				},
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.SHIPMENTS,
						],
					}),
					title: t('shipments'),
					icon: Truck,
				},
			],
		};

		if (config?.newsletterSubscriptionEnabled) {
			salesGroup.links.push({
				href: generatePath({
					route: [
						CLIENT_ROUTES.ADMIN.BASE,
						CLIENT_ROUTES.ADMIN.ROUTES.NEWS_LETTER,
					],
				}),
				title: t('newsletter'),
				icon: Mails,
			});
		}

		if (config?.analyticsEnabled) {
			salesGroup.links.push({
				href: generatePath({
					route: [
						CLIENT_ROUTES.ADMIN.BASE,
						CLIENT_ROUTES.ADMIN.ROUTES.ANALYTICS,
					],
				}),
				title: t('analytics'),
				icon: ChartLine,
			});
		}

		const configurationGroup = {
			title: t('configuration'),
			links: [
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.USERS,
						],
					}),
					title: t('users'),
					icon: Users,
				},
				{
					href: generatePath({
						route: [
							CLIENT_ROUTES.ADMIN.BASE,
							CLIENT_ROUTES.ADMIN.ROUTES.SETTINGS,
						],
					}),
					title: t('settings'),
					icon: Settings,
				},
			],
		};

		return [catalogGroup, salesGroup, configurationGroup];
	}, [t, config]);

	const resetMenusState = () => {
		setMenusOpen({});
		setIsMobileMenuOpen(false);
	};

	return (
		<header className="sticky top-0 z-50 bg-secondary-background border-b border-background-border">
			<div className="sm:px-6 lg:px-8 px-4 mx-auto">
				<div className="flex items-center justify-between h-16">
					<div className="flex items-center space-x-4">
						<HeaderImageTitle adminTitle />
					</div>

					<NavigationMenu className="md:flex hidden space-x-1">
						<NavigationMenuList>
							{adminGroupedLinks.map((group) => (
								<NavigationMenuItem key={group.title}>
									<NavigationMenuTrigger>
										{group.title}
									</NavigationMenuTrigger>
									<NavigationMenuContent>
										<ul className="grid gap-3 p-6 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
											{group.links.map((item) => {
												const IconComponent = item.icon;
												return (
													<Link
														className={cn(
															navigationMenuTriggerStyle(),
															'flex w-full space-x-2'
														)}
														to={item.href}>
														<div className="flex space-x-1">
															<IconComponent className="w-4 h-4" />
															<span>
																{item.title}
															</span>
														</div>
													</Link>
												);
											})}
										</ul>
									</NavigationMenuContent>
								</NavigationMenuItem>
							))}
						</NavigationMenuList>
					</NavigationMenu>

					{/* User Menu and Mobile Menu Button */}
					<div className="flex items-center space-x-4">
						{/* User Dropdown Menu */}
						<div className="md:block hidden">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="hover:text-primary relative p-2 text-sub-title transition-colors">
										<User className="w-6 h-6" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="end"
									className="w-48">
									<div className="px-2 py-1.5 text-sm font-medium text-secondary border-b border-placeholder-background">
										{user?.name}
									</div>
									<DropdownMenuItem asChild>
										<Link
											to="/admin/profile"
											className="flex items-center space-x-2 cursor-pointer">
											<UserCog className="w-4 h-4" />
											<span>{t('updateUser')}</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link
											to="/"
											className="flex items-center space-x-2 cursor-pointer">
											<Home className="w-4 h-4" />
											<span>{t('goToHome')}</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										onClick={handleLogout}
										className="hover:text-danger-hover flex items-center space-x-2 text-danger cursor-pointer">
										<LogOut className="w-4 h-4" />
										<span>{t('logout')}</span>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>

						{/* Mobile Menu Button */}
						<Button
							variant="ghost"
							size="icon"
							className="md:hidden"
							onClick={toggleMobileMenu}
							aria-label="Toggle mobile menu">
							{isMobileMenuOpen ? (
								<X className="w-6 h-6" />
							) : (
								<Menu className="w-6 h-6" />
							)}
						</Button>
					</div>
				</div>

				<AnimatePresence>
					{isMobileMenuOpen && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.3 }}
							className="md:hidden overflow-hidden bg-secondary-background border-t border-background-border">
							<div className="px-2 pt-2 pb-3 space-y-1">
								{adminGroupedLinks.map((group) => (
									<div key={group.title}>
										<button
											onClick={() =>
												setMenusOpen((prev) => ({
													...prev,
													[group.title]:
														!prev[group.title],
												}))
											}
											className="hover:bg-background flex items-center justify-between w-full px-3 py-2 text-base font-medium text-muted rounded-md">
											<span>{group.title}</span>
											{menusOpen[group.title] ? (
												<ChevronUp className="w-5 h-5" />
											) : (
												<ChevronDown className="w-5 h-5" />
											)}
										</button>
										<AnimatePresence>
											{menusOpen[group.title] && (
												<motion.div
													initial={{
														opacity: 0,
														height: 0,
													}}
													animate={{
														opacity: 1,
														height: 'auto',
													}}
													exit={{
														opacity: 0,
														height: 0,
													}}
													transition={{
														duration: 0.2,
													}}
													className="mt-2 ml-4 space-y-1 overflow-hidden">
													{group.links.map((item) => {
														const IconComponent =
															item.icon;
														return (
															<Link
																key={item.href}
																to={item.href}
																onClick={
																	resetMenusState
																}
																className="hover:bg-background block px-3 py-2 text-sm text-secondary rounded-md">
																<IconComponent className="w-4 h-4 inline-block me-2" />
																{item.title}
															</Link>
														);
													})}
												</motion.div>
											)}
										</AnimatePresence>
									</div>
								))}

								{/* Mobile User Menu */}
								<div className="pt-3 mt-3 border-t border-background-border">
									<div className="px-3 py-2 text-sm font-medium tracking-wide text-sub-title uppercase">
										{t('account')} - {user?.name}
									</div>
									<Link
										to={generatePath({
											route: [
												CLIENT_ROUTES.ADMIN.BASE,
												CLIENT_ROUTES.ADMIN.ROUTES
													.PROFILE,
											],
										})}
										className="hover:text-primary hover:bg-background flex items-center px-3 py-2 space-x-3 text-base font-medium text-secondary rounded-md"
										onClick={resetMenusState}>
										<UserCog className="w-5 h-5" />
										<span>{t('updateUser')}</span>
									</Link>
									<Link
										to={generatePath({
											route: [CLIENT_ROUTES.HOME],
										})}
										className="hover:text-primary hover:bg-background flex items-center px-3 py-2 space-x-3 text-base font-medium text-secondary rounded-md"
										onClick={resetMenusState}>
										<Home className="w-5 h-5" />
										<span>{t('goToHome')}</span>
									</Link>
									<button
										className="hover:text-danger-hover hover:bg-warning-background flex items-center w-full px-3 py-2 space-x-3 text-base font-medium text-left text-danger rounded-md"
										onClick={() => {
											resetMenusState();
											handleLogout();
										}}>
										<LogOut className="w-5 h-5" />
										<span>{t('logout')}</span>
									</button>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</header>
	);
}
